import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress, Snackbar, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  createCampaign,
  fetchCampaign,
  fetchCampaignBillDetail,
  getCampaignBillTypeLabel,
  buildDefaultCampaignEmail,
  buildEmailPreviewHtml,
  composeCampaignEmailPreview,
  openCampaignEmailDraft,
  parseEmailPreviewLine,
  prefillCampaignTargets,
  resolveCampaignBillUrl,
  searchCampaignBills,
  updateCampaign,
} from "../utils/campaignApi";
import { getPlanLimitUserFacing } from "../utils/billingApi";
import UpgradePrompt from "../components/AlertComponents/UpgradePrompt";
import { COST_PER_CALL, MIN_CALLS, TIMEZONE_OPTIONS } from "../constants/campaignConstants";
import {
  formatDateWithTimezone,
  getDefaultCampaignTimezone,
  getDurationLabel,
  parseTimezoneDateTimeValue,
  toTimezoneDateTimeValue,
} from "../utils/campaignDateUtils";
import {
  buildTakeActionCampaignFromDraft,
} from "../components/CampaignComponents/TakeActionCampaignContent";
import VerifyIdentityModal from "../components/VerifyIdentityModal";
import { saveTakeActionDraftPreview } from "../utils/campaignDraftPreview";
import {
  getStaleStartScheduleIssue,
  resolvePublishIssuesFromError,
} from "../utils/campaignPublishIssues";
import CampaignPublishBlockersDialog from "../components/CampaignComponents/CampaignPublishBlockersDialog";
import { useUser } from "../context/UserContext";
import "../styles/component_styles/Campaigns.scss";

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const emptyTarget = () => ({ name: "", title: "", phone: "", url: "" });

const nowPlusMinutes = (minutes) => new Date(Date.now() + minutes * 60 * 1000);

const digitsOnly = (phone) => String(phone || "").replace(/\D/g, "");
const MIN_DURATION_MS = 30 * 60 * 1000;
// Datetimes round-trip through the API as ISO strings, so ignore sub-second drift.
const END_DATE_TOLERANCE_MS = 1000;
const SCHEDULE_LOCK_NOTICE =
  "Once your campaign starts, the start time and time zone are locked so shared links and printed QR codes stay accurate. You can always extend the end date later.";

function isCampaignContentEditable(campaign) {
  if (!campaign) return true;
  const status = campaign.status || "draft";
  const verified = Boolean(campaign.verified_calling_enabled);
  if (status === "draft" || status === "pending_payment") return true;
  if (!verified && status === "active") return true;
  return false;
}

function canChangeVerifiedCallingSetting(campaignStatus) {
  return !campaignStatus || campaignStatus === "draft" || campaignStatus === "pending_payment";
}
const emptyLink = () => ({ name: "", description: "", url: "" });
const emptyExternalBill = () => ({ link_name: "", link_description: "", link_url: "" });
const isHttpUrl = (value) => /^https?:\/\/.+/i.test(String(value || "").trim());

const normalizeBillFromSearch = (item) => {
  const type = item.type || item.bill_type || "";
  const number = item.bill_number || item.number || "";
  const congress = item.congress ?? null;
  const id = `${String(type).toLowerCase()}-${number}`.toLowerCase();
  return {
    id,
    dbId: item.id ?? null,
    type,
    number,
    congress,
    title: item.title || getCampaignBillTypeLabel(type, number, congress),
  };
};

function getBillShortLabel(type, number) {
  const t = (type || "").toLowerCase();
  const num = number ? ` ${number}` : "";
  if (t === "hr") return `H.R.${num}`;
  if (t === "s") return `S.${num}`;
  if (t === "hjres") return `H.J.Res.${num}`;
  if (t === "sjres") return `S.J.Res.${num}`;
  if (t === "hconres") return `H.Con.Res.${num}`;
  if (t === "sconres") return `S.Con.Res.${num}`;
  return `${(type || "").toUpperCase()}${num}`;
}

function getBillChamberLabel(type) {
  const t = (type || "").toLowerCase();
  if (t.startsWith("h")) return "House";
  if (t.startsWith("s")) return "Senate";
  return "";
}

function getSelectedBillViewHref(bill) {
  if (!bill) return null;
  if (bill.dbId) return `/bills/${bill.dbId}`;
  if (bill.id) return `/bills/${bill.id}`;
  if (bill.type && bill.number) {
    return `/bills/${String(bill.type).toLowerCase()}-${bill.number}`;
  }
  return null;
}

function getResumeStep(location, stepList) {
  const valid = (key) => stepList.some((s) => s.key === key);
  const fromState = location.state?.resumeStep;
  if (typeof fromState === "string" && valid(fromState)) return fromState;
  const fromUrl = new URLSearchParams(location.search).get("step");
  if (fromUrl && valid(fromUrl)) return fromUrl;
  return null;
}

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, entityName, userFirstName, userLastName, userUsername, profileImage, isProfilePublic, loadUserContext, verifiedUser } = useUser();
  const [, setSearchParams] = useSearchParams();
  const { campaignId } = useParams();
  const isEditing = Boolean(campaignId);
  const resumeAppliedRef = useRef(false);

  const [stepKey, setStepKey] = useState("details");
  const [submitting, setSubmitting] = useState(false);
  const [loadingCampaign, setLoadingCampaign] = useState(isEditing);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });
  const [upgradePrompt, setUpgradePrompt] = useState({
    open: false,
    message: "",
    upgradeUrl: "/account-details#billing",
  });
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  const [exitSaving, setExitSaving] = useState(false);
  const [localCampaignId, setLocalCampaignId] = useState(campaignId || "");
  const [publishBlockersOpen, setPublishBlockersOpen] = useState(false);
  const [publishIssues, setPublishIssues] = useState([]);
  const [formDirty, setFormDirty] = useState(!isEditing);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyHintExpanded, setVerifyHintExpanded] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [actionScript, setActionScript] = useState("");
  const [additionalLinks, setAdditionalLinks] = useState([emptyLink()]);
  const [verifiedCalling, setVerifiedCalling] = useState(false);
  const [repSource, setRepSource] = useState("custom_list");
  const [customTargets, setCustomTargets] = useState([emptyTarget()]);
  const [prefillModalOpen, setPrefillModalOpen] = useState(false);
  const [prefillState, setPrefillState] = useState("");
  const [prefillDistrict, setPrefillDistrict] = useState("");
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [billMode, setBillMode] = useState("in_app");
  const [selectedBill, setSelectedBill] = useState(null);
  const [externalBill, setExternalBill] = useState(emptyExternalBill());
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [startDate, setStartDate] = useState(nowPlusMinutes(10));
  const [endDate, setEndDate] = useState(nowPlusMinutes(40));
  const [maxCallsText, setMaxCallsText] = useState("100");
  const [selectedTimezone, setSelectedTimezone] = useState(getDefaultCampaignTimezone);
  const [scheduleLocked, setScheduleLocked] = useState(false);
  const [originalEndDate, setOriginalEndDate] = useState(null);
  const [loadedCampaignStatus, setLoadedCampaignStatus] = useState(null);

  const [billQuery, setBillQuery] = useState("");
  const [billResults, setBillResults] = useState([]);
  const [searchingBills, setSearchingBills] = useState(false);
  const [billSearchError, setBillSearchError] = useState("");
  const emailSeededRef = useRef(false);
  const formHydratedRef = useRef(false);

  const maxCalls = useMemo(() => parseInt(maxCallsText, 10) || MIN_CALLS, [maxCallsText]);
  const totalCost = useMemo(() => maxCalls * COST_PER_CALL, [maxCalls]);
  const startInPast = startDate.getTime() <= Date.now();
  // A started campaign's start time and timezone are already on printed QR flyers
  // and shared links, so only the end date stays editable — and only later.
  const minEndTime =
    scheduleLocked && originalEndDate
      ? originalEndDate.getTime()
      : startDate.getTime() + MIN_DURATION_MS;
  const endDateMoved =
    Boolean(originalEndDate) &&
    Math.abs(endDate.getTime() - originalEndDate.getTime()) > END_DATE_TOLERANCE_MS;
  let lockedEndError = "";
  if (scheduleLocked && endDateMoved) {
    if (endDate.getTime() < originalEndDate.getTime()) {
      lockedEndError =
        "The end date can be extended but not moved earlier. Stop the campaign if you need it to end now.";
    } else if (endDate.getTime() <= Date.now()) {
      lockedEndError = "Pick an end date in the future.";
    }
  }

  // Match mobile/web parity: Details → Bill → Preview → Schedule → [Budget] → Review
  const steps = useMemo(() => {
    const list = [
      { key: "details", title: "Details" },
      { key: "bill", title: "Bill" },
      { key: "preview", title: "Preview" },
      { key: "schedule", title: "Schedule" },
    ];
    if (verifiedCalling) list.push({ key: "budget", title: "Budget" });
    list.push({ key: "review", title: "Review" });
    return list;
  }, [verifiedCalling]);

  const stepIndex = Math.max(0, steps.findIndex((s) => s.key === stepKey));
  const exitDestination = campaignId ? `/campaigns/${campaignId}` : "/campaigns";

  const goToStep = useCallback(
    (nextStep) => {
      if (!steps.some((s) => s.key === nextStep)) return;
      setStepKey(nextStep);
      if (campaignId) {
        setSearchParams({ step: nextStep }, { replace: true });
      }
    },
    [campaignId, setSearchParams, steps]
  );

  useEffect(() => {
    if (!verifiedCalling && stepKey === "budget") goToStep("review");
    if (stepKey === "targets") goToStep("details");
  }, [verifiedCalling, stepKey, goToStep]);

  useEffect(() => {
    if (stepKey !== "preview") return;
    if (typeof loadUserContext === "function") {
      loadUserContext();
    }
  }, [stepKey, loadUserContext]);

  useEffect(() => {
    if (stepKey !== "preview") return;
    if (emailSeededRef.current) return;
    if ((emailSubject || "").trim() || (emailBody || "").trim()) {
      emailSeededRef.current = true;
      return;
    }
    const draft = buildDefaultCampaignEmail({ name, description, actionScript });
    setEmailSubject(draft.subject);
    setEmailBody(draft.body);
    emailSeededRef.current = true;
  }, [stepKey, name, description, actionScript, emailSubject, emailBody]);

  useEffect(() => {
    setEndDate((prev) => {
      const minEndDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      return prev.getTime() < minEndDate.getTime() ? minEndDate : prev;
    });
  }, [startDate]);

  useEffect(() => {
    if (!billQuery.trim()) {
      setBillResults([]);
      setBillSearchError("");
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearchingBills(true);
      setBillSearchError("");
      try {
        const response = await searchCampaignBills(billQuery.trim());
        setBillResults((response.bills || []).map(normalizeBillFromSearch));
      } catch {
        setBillResults([]);
        setBillSearchError("Bill search failed. Check your connection and try again.");
      } finally {
        setSearchingBills(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [billQuery]);

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;

    const load = async () => {
      setLoadingCampaign(true);
      try {
        const data = await fetchCampaign(campaignId);
        if (cancelled) return;
        if (!isCampaignContentEditable(data)) {
          navigate(`/campaigns/${campaignId}`, {
            state: {
              flash: "This campaign can no longer be edited. Stop it first if you need to make changes.",
              flashSeverity: "error",
            },
          });
          return;
        }
        setLoadedCampaignStatus(data.status || "draft");
        setName(data.name || "");
        setDescription(data.description || "");
        setActionScript(data.action_script || "");
        setEmailSubject(data.email_subject || "");
        setEmailBody(data.email_body || "");
        setAdditionalLinks(
          Array.isArray(data.additional_links) && data.additional_links.length > 0
            ? data.additional_links.map((link) => ({
                name: link.name || "",
                description: link.description || "",
                url: link.url || "",
              }))
            : [emptyLink()]
        );
        if (data.bill_mode === "external") {
          setBillMode("external");
          setExternalBill({
            link_name: data?.bill?.link_name || "",
            link_description: data?.bill?.link_description || "",
            link_url: data?.bill?.link_url || "",
          });
          setSelectedBill(null);
        } else {
          setBillMode("in_app");
          setExternalBill(emptyExternalBill());
        }
        setVerifiedCalling(Boolean(data.verified_calling_enabled));
        setRepSource(data.rep_source || "custom_list");
        setCustomTargets(
          Array.isArray(data.custom_targets) && data.custom_targets.length > 0
            ? data.custom_targets.map((t) => ({
                name: t.name || "",
                title: t.title || "",
                phone: t.phone || "",
                url: t.url || "",
              }))
            : [emptyTarget()]
        );
        if (data.start_datetime) setStartDate(new Date(data.start_datetime));
        if (data.end_datetime) {
          setEndDate(new Date(data.end_datetime));
          setOriginalEndDate(new Date(data.end_datetime));
        }
        setScheduleLocked(Boolean(data.schedule_locked));
        if (data.timezone) setSelectedTimezone(data.timezone);
        if (data.max_calls) setMaxCallsText(String(data.max_calls));

        if ((data.email_subject || "").trim() || (data.email_body || "").trim()) {
          emailSeededRef.current = true;
        } else {
          emailSeededRef.current = false;
        }

        if ((data.bill_mode || "in_app") === "in_app" && data.target_bill_type && data.target_bill_number) {
          let title = getCampaignBillTypeLabel(
            data.target_bill_type,
            data.target_bill_number,
            data.target_bill_congress
          );
          if (data.target_bill_db_id) {
            try {
              const bill = await fetchCampaignBillDetail(data.target_bill_db_id);
              if (bill?.title) title = bill.title;
            } catch {
              // Use fallback title label.
            }
          }
          setSelectedBill({
            id:
              data.target_bill_id ||
              `${String(data.target_bill_type).toLowerCase()}-${data.target_bill_number}`.toLowerCase(),
            dbId: data.target_bill_db_id || null,
            type: data.target_bill_type,
            number: data.target_bill_number,
            congress: data.target_bill_congress,
            title,
          });
        }
      } catch {
        if (!cancelled) {
          navigate("/campaigns", {
            state: { flash: "Failed to load campaign for editing.", flashSeverity: "error" },
          });
        }
      } finally {
        if (!cancelled) setLoadingCampaign(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId, isEditing, navigate]);

  useEffect(() => {
    if (!isEditing || loadingCampaign || resumeAppliedRef.current) return;
    const resume = getResumeStep(location, steps);
    if (!resume) return;
    resumeAppliedRef.current = true;
    setStepKey(resume);
  }, [isEditing, loadingCampaign, steps, location]);

  const validTargets = useMemo(
    () =>
      customTargets
        .filter((t) => (t.name || "").trim() && digitsOnly(t.phone).length >= 10)
        .map((t) => ({
          name: (t.name || "").trim(),
          title: (t.title || "").trim(),
          phone: (t.phone || "").trim(),
          url: (t.url || "").trim(),
        })),
    [customTargets]
  );
  const validLinks = useMemo(
    () =>
      additionalLinks
        .map((l) => ({
          name: (l.name || "").trim(),
          description: (l.description || "").trim(),
          url: (l.url || "").trim(),
        }))
        .filter((l) => l.name && isHttpUrl(l.url))
        .slice(0, 5),
    [additionalLinks]
  );

  const canProceed = () => {
    switch (stepKey) {
      case "details":
        if (name.trim().length < 3) return false;
        if (!(actionScript || "").trim()) return false;
        if (repSource === "custom_list") return validTargets.length >= 1;
        return true;
      case "bill":
        if (billMode === "external") {
          return (
            (externalBill.link_name || "").trim().length >= 2 &&
            isHttpUrl(externalBill.link_url)
          );
        }
        return Boolean(selectedBill);
      case "preview":
        return (emailSubject || "").trim().length > 0 && (emailBody || "").trim().length > 0;
      case "schedule":
        if (endDate.getTime() - startDate.getTime() < MIN_DURATION_MS) return false;
        if (scheduleLocked) return !lockedEndError;
        return startDate.getTime() > Date.now();
      case "budget":
        return parseInt(maxCallsText, 10) >= MIN_CALLS;
      default:
        return true;
    }
  };
  const effectiveCampaignId = localCampaignId || campaignId || "";
  const canSaveDraftOnCancel =
    Boolean(effectiveCampaignId) ||
    (name.trim().length > 0 &&
      (repSource === "viewer_reps" || validTargets.length >= 1));

  const isDraftLikeStatus =
    !loadedCampaignStatus ||
    loadedCampaignStatus === "draft" ||
    loadedCampaignStatus === "pending_payment";

  const openPublishBlockers = (issues) => {
    setPublishIssues(issues);
    setPublishBlockersOpen(true);
  };

  const guardStaleScheduleBeforeSave = () => {
    const staleIssue = getStaleStartScheduleIssue(scheduleLocked, startDate);
    if (!staleIssue) return true;
    openPublishBlockers([staleIssue]);
    goToStep("schedule");
    return false;
  };

  useEffect(() => {
    setLocalCampaignId(campaignId || "");
    formHydratedRef.current = false;
  }, [campaignId]);

  useEffect(() => {
    if (loadingCampaign) return;
    if (!formHydratedRef.current) {
      formHydratedRef.current = true;
      if (isEditing) setFormDirty(false);
      return;
    }
    setFormDirty(true);
  }, [
    loadingCampaign,
    isEditing,
    name,
    description,
    actionScript,
    emailSubject,
    emailBody,
    repSource,
    customTargets,
    billMode,
    selectedBill,
    externalBill,
    startDate,
    endDate,
    maxCallsText,
    selectedTimezone,
    verifiedCalling,
    additionalLinks,
  ]);

  useEffect(() => {
    if (!formDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [formDirty]);

  const buildCampaignPayload = () => {
    const payload = {
    name: name.trim(),
    description: description.trim(),
    action_script: actionScript.trim(),
    email_subject: emailSubject.trim(),
    email_body: emailBody.trim(),
    additional_links: validLinks,
    verified_calling_enabled: verifiedCalling,
    rep_source: repSource,
    custom_targets: repSource === "custom_list" ? validTargets : [],
    target_bill_id: selectedBill?.id || null,
    target_bill_type: selectedBill?.type || null,
    target_bill_number: selectedBill?.number || null,
    target_bill_congress: selectedBill?.congress || null,
    target_bill_db_id: selectedBill?.dbId || null,
    bill_mode: billMode,
    bill:
      billMode === "external"
        ? {
            link_name: (externalBill.link_name || "").trim(),
            link_description: (externalBill.link_description || "").trim(),
            link_url: (externalBill.link_url || "").trim(),
          }
        : {
            bill_id: selectedBill?.dbId || null,
            bill_type: selectedBill?.type || null,
            bill_number: selectedBill?.number || null,
            congress: selectedBill?.congress || null,
            jurisdiction: "federal",
          },
    start_datetime: startDate.toISOString(),
    end_datetime: endDate.toISOString(),
    timezone: selectedTimezone,
    max_calls: verifiedCalling ? maxCalls : 100,
    cost_per_call: verifiedCalling ? COST_PER_CALL : 0,
    };
    return payload;
  };

  const persistCampaignContent = async () => {
    const payload = buildCampaignPayload();
    const existingId = (localCampaignId || campaignId || "").trim();
    if (existingId) {
      await updateCampaign(existingId, payload);
      return existingId;
    }
    const created = await createCampaign(payload);
    const createdId = String(created?.id || "").trim();
    if (!createdId) {
      throw new Error("Campaign was saved but no campaign id was returned.");
    }
    setLocalCampaignId(createdId);
    return createdId;
  };

  const publishFreeCampaign = async (id) => {
    await updateCampaign(id, { status: "active" });
  };

  const showPublishBlockers = (error) => {
    const issues = resolvePublishIssuesFromError(error);
    if (!issues || issues.length === 0) return false;
    openPublishBlockers(issues);
    return true;
  };

  const showApiError = (error, fallbackMessage) => {
    if (showPublishBlockers(error)) return;
    const planLimit = getPlanLimitUserFacing(error);
    if (planLimit) {
      setUpgradePrompt({
        open: true,
        message: planLimit.message,
        upgradeUrl: planLimit.upgradeHref,
      });
      return;
    }
    setSnack({
      open: true,
      severity: "error",
      message: error?.response?.data?.error || fallbackMessage,
    });
  };

  const handleSubmit = async () => {
    if (!guardStaleScheduleBeforeSave()) return;
    setSubmitting(true);
    try {
      const id = await persistCampaignContent();
      if (!verifiedCalling && isDraftLikeStatus) {
        await publishFreeCampaign(id);
      }
      setFormDirty(false);
      const publishingFreeDraft = !verifiedCalling && loadedCampaignStatus === "draft";
      navigate(`/campaigns/${id}`, {
        state: {
          flash: verifiedCalling
            ? isEditing
              ? "Changes saved. Complete payment if this campaign still needs activation."
              : "Campaign created. Complete payment to activate verified calling."
            : publishingFreeDraft
              ? "Your campaign is live. Share the take-action link from this page."
              : isEditing
                ? "Changes saved. Share the take-action link from the campaign page."
                : "Your campaign is live. Share the take-action link from this page.",
        },
      });
    } catch (error) {
      if (showPublishBlockers(error)) {
        const issues = resolvePublishIssuesFromError(error);
        if (issues?.some((i) => i.step === "schedule")) {
          goToStep("schedule");
        }
        return;
      }
      showApiError(error, `Failed to ${isEditing ? "update" : "create"} campaign.`);
    } finally {
      setSubmitting(false);
    }
  };

  const onNext = async () => {
    if (!canProceed() || submitting) return;

    if (stepKey === "review") {
      await handleSubmit();
      return;
    }

    setFormDirty(true);
    goToStep(steps[stepIndex + 1].key);
  };

  const onBack = () => {
    if (stepIndex > 0) {
      goToStep(steps[stepIndex - 1].key);
      return;
    }
    setExitDialogOpen(true);
  };

  const handleDiscardExit = () => {
    setExitDialogOpen(false);
    navigate(exitDestination);
  };

  const handleSaveDraftExit = async () => {
    if (!canSaveDraftOnCancel || exitSaving) return;
    if (!guardStaleScheduleBeforeSave()) return;
    setExitSaving(true);
    try {
      const id = await persistCampaignContent();
      setFormDirty(false);
      setExitDialogOpen(false);
      navigate(`/campaigns/${id}`, {
        state: { flash: "Draft saved. You can continue editing anytime." },
      });
    } catch (error) {
      if (showPublishBlockers(error)) {
        const issues = resolvePublishIssuesFromError(error);
        if (issues?.some((i) => i.step === "schedule")) {
          goToStep("schedule");
        }
        return;
      }
      showApiError(error, "Could not save draft before exiting.");
    } finally {
      setExitSaving(false);
    }
  };

  const updateTarget = (index, field, value) => {
    setCustomTargets((prev) =>
      prev.map((t, i) => (i === index ? { ...t, [field]: value } : t))
    );
  };
  const handleCopyPreview = async (preview) => {
    const draft = `Subject: ${preview.subject}\n\n${preview.body}`;
    try {
      if (typeof window !== "undefined" && window.ClipboardItem && navigator.clipboard?.write) {
        const html = buildEmailPreviewHtml({ subject: preview.subject, body: preview.body, name });
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/plain": new Blob([draft], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(draft);
      }
      setSnack({ open: true, severity: "success", message: "Email preview copied." });
    } catch {
      try {
        await navigator.clipboard.writeText(draft);
        setSnack({ open: true, severity: "success", message: "Email preview copied." });
      } catch {
        setSnack({ open: true, severity: "error", message: "Could not copy email preview." });
      }
    }
  };
  const handleOpenEmailApp = (preview) => {
    try {
      const result = openCampaignEmailDraft({
        subject: preview.subject || "",
        body: preview.body || "",
      });
      if (result.truncated) {
        setSnack({
          open: true,
          severity: "info",
          message: "Email body was shortened to fit your mail app. Use Copy for the full draft.",
        });
      }
    } catch {
      setSnack({
        open: true,
        severity: "error",
        message: "Could not open your email app. Try Copy instead.",
      });
    }
  };
  const handlePrefillTargets = () => {
    setPrefillState("");
    setPrefillDistrict("");
    setPrefillModalOpen(true);
  };

  const executePrefillTargets = async () => {
    const st = prefillState.trim().toUpperCase();
    if (!st || st.length !== 2) {
      setSnack({ open: true, severity: "error", message: "Please enter a valid 2-letter state code." });
      return;
    }
    setPrefillLoading(true);
    try {
      const data = await prefillCampaignTargets(st, prefillDistrict.trim());
      if (!Array.isArray(data.targets) || data.targets.length === 0) {
        setSnack({ open: true, severity: "info", message: "No Congress targets found for that state/district." });
      } else {
        setCustomTargets(
          data.targets.slice(0, 20).map((t) => ({
            name: t.name || "",
            title: t.title || "",
            phone: t.phone || "",
            url: t.url || "",
          }))
        );
        setSnack({
          open: true,
          severity: "success",
          message: data.note || "Targets prefilled. Please manually confirm all numbers before publishing.",
        });
        setPrefillModalOpen(false);
      }
    } catch (error) {
      setSnack({
        open: true,
        severity: "error",
        message: error?.response?.data?.error || "Failed to prefill targets.",
      });
    } finally {
      setPrefillLoading(false);
    }
  };

  const verifiedCallingLocked = !canChangeVerifiedCallingSetting(loadedCampaignStatus);

  const renderDetails = () => (
    <>
      <div className="campaign-field">
        <label htmlFor="campaign-name">Campaign Name *</label>
        <input
          id="campaign-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          placeholder="e.g., Support HR 1234 - Clean Energy Act"
        />
        <small>{name.length}/200</small>
      </div>

      <div className="campaign-field">
        <label htmlFor="campaign-description">Description</label>
        <textarea
          id="campaign-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          rows={4}
          placeholder="Describe the goals of this campaign..."
        />
        <small>{description.length}/1000</small>
      </div>

      <div className="campaign-field campaign-create__related-links-intro">
        <label>Related Links</label>
        <small>Add up to 5 links supporters might need (voter registration, fact sheet, etc.).</small>
      </div>
      <div className="campaign-create__targets campaign-create__targets--links">
      {additionalLinks.map((link, index) => (
        <div key={`link-${index}`} className="campaign-create__target-card">
          <div className="campaign-field">
            <label htmlFor={`link-name-${index}`}>Link name</label>
            <input
              id={`link-name-${index}`}
              value={link.name}
              onChange={(e) =>
                setAdditionalLinks((prev) =>
                  prev.map((item, i) => (i === index ? { ...item, name: e.target.value } : item))
                )
              }
              placeholder="e.g., Check voter registration"
            />
          </div>
          <div className="campaign-field">
            <label htmlFor={`link-description-${index}`}>Description</label>
            <input
              id={`link-description-${index}`}
              value={link.description}
              onChange={(e) =>
                setAdditionalLinks((prev) =>
                  prev.map((item, i) => (i === index ? { ...item, description: e.target.value } : item))
                )
              }
              placeholder="Short context for supporters"
            />
          </div>
          <div className="campaign-field" style={{ marginBottom: 0 }}>
            <label htmlFor={`link-url-${index}`}>URL</label>
            <input
              id={`link-url-${index}`}
              value={link.url}
              onChange={(e) =>
                setAdditionalLinks((prev) =>
                  prev.map((item, i) => (i === index ? { ...item, url: e.target.value } : item))
                )
              }
              placeholder="https://example.org/resource"
              aria-invalid={Boolean(link.url.trim() && !isHttpUrl(link.url))}
            />
            {link.url.trim() && !isHttpUrl(link.url) ? (
              <p className="campaign-field-error" role="alert">
                Use a full http(s) URL.
              </p>
            ) : null}
          </div>
          {additionalLinks.length > 1 ? (
            <button
              type="button"
              className="campaign-create__target-remove"
              onClick={() => setAdditionalLinks((prev) => prev.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          ) : null}
        </div>
      ))}
      {additionalLinks.length < 5 ? (
        <div className="campaign-create__targets-actions">
          <button
            type="button"
            className="campaign-create__add-target"
            onClick={() => setAdditionalLinks((prev) => [...prev, emptyLink()])}
          >
            <span className="campaign-create__add-target-icon" aria-hidden="true">+</span>
            Add related link
          </button>
        </div>
      ) : null}
      </div>

      <div className="campaign-field">
        <label htmlFor="action-script">What should people say? *</label>
        <textarea
          id="action-script"
          value={actionScript}
          onChange={(e) => setActionScript(e.target.value.slice(0, 5000))}
          rows={5}
          placeholder="Write a short script callers can use..."
        />
        <small>{actionScript.length}/5000</small>
      </div>

      <div className="campaign-create__switch-block">
        <div className="campaign-create__switch-label">Enable verified calling (paid)</div>
        <div className="campaign-create__switch-row">
          <div className="campaign-create__switch-sublabel">
            Adds tracked patch-through calls with a per-call budget. Leave off for a free take-action page.
            {verifiedCallingLocked ? (
              <> Verified calling can only be enabled before a campaign goes live.</>
            ) : null}
          </div>
          <Switch
            checked={verifiedCalling}
            disabled={verifiedCallingLocked}
            onChange={(e) => setVerifiedCalling(e.target.checked)}
            inputProps={{ "aria-label": "Enable verified calling" }}
            sx={{
              width: 58,
              height: 34,
              padding: 0,
              flexShrink: 0,
              "& .MuiSwitch-switchBase": {
                padding: 0,
                margin: "5px",
                transitionDuration: "200ms",
                "&.Mui-checked": {
                  transform: "translateX(24px)",
                  color: "#fff",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#047857",
                    opacity: 1,
                    border: 0,
                  },
                },
              },
              "& .MuiSwitch-thumb": {
                boxSizing: "border-box",
                width: 24,
                height: 24,
              },
              "& .MuiSwitch-track": {
                borderRadius: 17,
                backgroundColor: "#cbd5e1",
                opacity: 1,
              },
            }}
          />
        </div>
      </div>

      <div className="campaign-field campaign-create__who-to-call">
        <label>Who should they call? *</label>
        <div className="campaign-create__rep-source">
          <button
            type="button"
            className={`campaign-create__rep-option ${repSource === "custom_list" ? "is-active" : ""}`}
            onClick={() => setRepSource("custom_list")}
          >
            Custom list
          </button>
          <button
            type="button"
            className={`campaign-create__rep-option ${repSource === "viewer_reps" ? "is-active" : ""}`}
            onClick={() => setRepSource("viewer_reps")}
          >
            Viewer&apos;s reps
          </button>
        </div>
        {repSource === "custom_list" ? (
          <p className="campaign-create__rep-note">
            You provide the names and phone numbers everyone will see.
          </p>
        ) : (
          <div className="campaign-create__viewer-reps-note" role="note">
            <p className="campaign-create__viewer-reps-note-title">
              Callers enter their address to see their House rep and senators.
            </p>
            <p className="campaign-create__viewer-reps-note-body">
              Works for everyone, including people who aren&apos;t logged in. Share the take-action
              link and they can look up their own reps right on the page.
            </p>
          </div>
        )}
      </div>

      {repSource === "custom_list" ? (
        <div className="campaign-create__targets">
          <div className="campaign-create__targets-notice">
            <strong>Note:</strong> If you prefilled targets from Congress, it's always worth manually validating the phone numbers and website links before publishing. You can edit any value below.
          </div>
          <div className="campaign-create__targets-actions campaign-create__targets-actions--prefill">
            <button
              type="button"
              className="campaign-create__add-target"
              onClick={handlePrefillTargets}
            >
              <span className="campaign-create__add-target-icon" aria-hidden="true">+</span>
              Prefill from Congress
            </button>
          </div>
          {customTargets.map((t, index) => (
            <div key={index} className="campaign-create__target-card">
              <div className="campaign-field">
                <label htmlFor={`target-name-${index}`}>Full name *</label>
                <input
                  id={`target-name-${index}`}
                  value={t.name}
                  onChange={(e) => updateTarget(index, "name", e.target.value)}
                  placeholder="e.g., Jane Doe"
                />
              </div>
              <div className="campaign-field">
                <label htmlFor={`target-title-${index}`}>Title</label>
                <input
                  id={`target-title-${index}`}
                  value={t.title}
                  onChange={(e) => updateTarget(index, "title", e.target.value)}
                  placeholder="e.g., Representative"
                />
              </div>
              <div className="campaign-field">
                <label htmlFor={`target-phone-${index}`}>Phone *</label>
                <input
                  id={`target-phone-${index}`}
                  value={t.phone}
                  onChange={(e) => updateTarget(index, "phone", e.target.value)}
                  placeholder="(202) 555-0100"
                  inputMode="tel"
                />
                {t.phone.trim() && digitsOnly(t.phone).length < 10 ? (
                  <p className="campaign-field-error" role="alert">Enter at least 10 digits</p>
                ) : null}
              </div>
              <div className="campaign-field" style={{ marginBottom: 0 }}>
                <label htmlFor={`target-url-${index}`}>Website URL</label>
                <input
                  id={`target-url-${index}`}
                  value={t.url || ""}
                  onChange={(e) => updateTarget(index, "url", e.target.value)}
                  placeholder="e.g., https://johndoe.house.gov"
                  type="url"
                />
              </div>
              {customTargets.length > 1 ? (
                <button
                  type="button"
                  className="campaign-create__target-remove"
                  onClick={() => setCustomTargets((prev) => prev.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              ) : null}
            </div>
          ))}
          {customTargets.length < 20 ? (
            <div className="campaign-create__targets-actions">
              <button
                type="button"
                className="campaign-create__add-target"
                onClick={() => setCustomTargets((prev) => [...prev, emptyTarget()])}
              >
                <span className="campaign-create__add-target-icon" aria-hidden="true">+</span>
                Add another target
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const renderBill = () => {
    const selectedBillHref = selectedBill ? getSelectedBillViewHref(selectedBill) : null;

    return (
    <>
      <h2 className="campaign-create__step-title">Target Bill</h2>
      <p className="campaign-create__step-subtitle">
        Choose one mode: in-app bill lookup or a single external bill link.
      </p>
      <div className="campaign-create__rep-source">
        <button
          type="button"
          className={`campaign-create__rep-option ${billMode === "in_app" ? "is-active" : ""}`}
          onClick={() => setBillMode("in_app")}
        >
          In-app bill
        </button>
        <button
          type="button"
          className={`campaign-create__rep-option ${billMode === "external" ? "is-active" : ""}`}
          onClick={() => setBillMode("external")}
        >
          External bill link
        </button>
      </div>

      {billMode === "in_app" && selectedBill ? (
        <div className="campaign-create__selected-bill">
          <div className="campaign-create__selected-bill-top">
            <div>
              <div className="campaign-create__selected-bill-id">
                {getBillShortLabel(selectedBill.type, selectedBill.number)}
              </div>
              <div className="campaign-create__selected-bill-meta">
                {getBillChamberLabel(selectedBill.type)}
                {selectedBill.congress ? ` · ${selectedBill.congress}th Congress` : ""}
              </div>
            </div>
            <button
              type="button"
              className="campaign-create__clear-bill"
              onClick={() => {
                setSelectedBill(null);
                setBillQuery("");
                setBillResults([]);
              }}
              aria-label="Remove selected bill"
            >
              ×
            </button>
          </div>
          <p className="campaign-create__selected-bill-title">{selectedBill.title}</p>
          <div className="campaign-create__selected-bill-actions">
            {selectedBillHref ? (
              <a
                href={selectedBillHref}
                target="_blank"
                rel="noopener noreferrer"
                className="campaign-btn campaign-btn--primary"
              >
                View bill
              </a>
            ) : null}
            <button
              type="button"
              className="campaign-btn"
              onClick={() => {
                setSelectedBill(null);
                setBillQuery("");
              }}
            >
              Change bill
            </button>
          </div>
        </div>
      ) : null}
      {billMode === "in_app" && !selectedBill ? (
        <>
          <div className="campaign-field">
            <label htmlFor="bill-query">Search bill number or title</label>
            <input
              id="bill-query"
              value={billQuery}
              onChange={(e) => setBillQuery(e.target.value)}
              placeholder="Search by bill number or title..."
            />
            <small className="campaign-filter-note">
              Search by bill number (e.g., HR 10) or title. Results prioritize the 119th Congress.
            </small>
          </div>
          {searchingBills ? <p className="campaign-filter-note">Searching bills...</p> : null}
          {billSearchError ? (
            <p className="campaign-field-error" role="alert">
              {billSearchError}
            </p>
          ) : null}
          {billResults.length > 0 ? (
            <div className="campaign-bill-results">
              {billResults.map((bill) => (
                <div
                  key={`${bill.id}-${bill.congress || "na"}`}
                  className="campaign-bill-option"
                  onClick={() => setSelectedBill(bill)}
                  onKeyDown={(e) => e.key === "Enter" && setSelectedBill(bill)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="campaign-bill-option__title">
                    {getBillShortLabel(bill.type, bill.number)}
                    <span className="campaign-bill-option__chamber">
                      {" "}
                      · {getBillChamberLabel(bill.type)}
                      {bill.congress ? ` · ${bill.congress}th` : ""}
                    </span>
                  </div>
                  <div className="campaign-bill-option__meta">{bill.title}</div>
                </div>
              ))}
            </div>
          ) : billQuery.trim() && !searchingBills ? (
            <p className="campaign-filter-note">No bills found. Try a different search term.</p>
          ) : null}
        </>
      ) : null}
      {billMode === "external" ? (
        <div className="campaign-create__target-card">
          <div className="campaign-field">
            <label htmlFor="external-bill-name">Link name *</label>
            <input
              id="external-bill-name"
              value={externalBill.link_name}
              onChange={(e) => setExternalBill((prev) => ({ ...prev, link_name: e.target.value }))}
              placeholder="e.g., Read the bill text"
            />
          </div>
          <div className="campaign-field">
            <label htmlFor="external-bill-description">Description</label>
            <input
              id="external-bill-description"
              value={externalBill.link_description}
              onChange={(e) => setExternalBill((prev) => ({ ...prev, link_description: e.target.value }))}
              placeholder="Optional context for supporters"
            />
          </div>
          <div className="campaign-field" style={{ marginBottom: 0 }}>
            <label htmlFor="external-bill-url">Link URL *</label>
            <input
              id="external-bill-url"
              value={externalBill.link_url}
              onChange={(e) => setExternalBill((prev) => ({ ...prev, link_url: e.target.value }))}
              placeholder="https://..."
              aria-invalid={Boolean(externalBill.link_url.trim() && !isHttpUrl(externalBill.link_url))}
            />
            {externalBill.link_url.trim() && !isHttpUrl(externalBill.link_url) ? (
              <p className="campaign-field-error" role="alert">Use a full http(s) URL.</p>
            ) : null}
          </div>
          {isHttpUrl(externalBill.link_url) ? (
            <div className="campaign-create__selected-bill-actions" style={{ marginTop: 12 }}>
              <a
                href={externalBill.link_url.trim()}
                target="_blank"
                rel="noopener noreferrer"
                className="campaign-btn campaign-btn--primary"
              >
                View bill link
              </a>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
    );
  };

  const previewCampaign = useMemo(
    () => {
      const organizerDisplayName =
        (entityName || "").trim() ||
        `${(userFirstName || "").trim()} ${(userLastName || "").trim()}`.trim() ||
        (userUsername || "").trim();
      return buildTakeActionCampaignFromDraft({
        name,
        description,
        actionScript,
        organizerName: organizerDisplayName,
        organizerId: userId || null,
        organizerProfileImage: profileImage || null,
        organizerProfilePublic: Boolean(isProfilePublic),
        organizerIsVerified: Boolean(verifiedUser),
        billMode,
        selectedBill,
        externalBill,
        billDbId: selectedBill?.dbId || null,
        additionalLinks: validLinks,
        repSource,
        customTargets: validTargets,
      });
    },
    [
      name,
      description,
      actionScript,
      entityName,
      userFirstName,
      userLastName,
      userUsername,
      userId,
      profileImage,
      isProfilePublic,
      verifiedUser,
      billMode,
      selectedBill,
      externalBill,
      validLinks,
      repSource,
      validTargets,
    ]
  );

  const previewDisplayTargets = useMemo(() => {
    if (repSource === "custom_list") {
      return validTargets.map((t) => ({
        name: t.name,
        title: t.title,
        phone: t.phone,
        url: t.url,
      }));
    }
    return [];
  }, [repSource, validTargets]);

  const handleViewTakeActionPreview = () => {
    const saved = saveTakeActionDraftPreview({
      campaign: previewCampaign,
      displayTargets: previewDisplayTargets,
    });
    if (!saved) {
      setSnack({
        open: true,
        severity: "error",
        message: "Could not open take-action preview. Try again.",
      });
      return;
    }
    window.open("/campaigns/preview/take-action", "_blank", "noopener,noreferrer");
  };

  const renderPreview = () => {
    const shareUrlPreview = `https://www.civixthread.com/take-action/${campaignId || "<campaign-id>"}`;
    const billLine =
      billMode === "external"
        ? (externalBill.link_name || "").trim()
        : selectedBill
          ? getCampaignBillTypeLabel(selectedBill.type, selectedBill.number, selectedBill.congress)
          : "";
    const billUrl = resolveCampaignBillUrl({
      billMode,
      billDbId: selectedBill?.dbId || null,
      externalBillUrl: externalBill.link_url,
    });
    const emailPreview = composeCampaignEmailPreview({
      emailSubject,
      emailBody,
      name,
      description,
      actionScript,
      billLine,
      billUrl,
      relatedLinks: validLinks,
      targets: validTargets,
      shareUrl: shareUrlPreview,
    });

    return (
      <>
        <h2 className="campaign-create__step-title">Preview</h2>
        <p className="campaign-create__step-subtitle">
          Draft the supporter email and review the take-action page supporters will open from that link.
        </p>
        <div className="campaign-field">
          <label htmlFor="email-subject">Email subject *</label>
          <input
            id="email-subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value.slice(0, 200))}
            placeholder="Take action today: {{campaign_name}}"
          />
          <small>{emailSubject.length}/200</small>
        </div>
        <div className="campaign-field">
          <label htmlFor="email-body">Email body *</label>
          <textarea
            id="email-body"
            value={emailBody}
            onChange={(e) => setEmailBody(e.target.value.slice(0, 5000))}
            rows={8}
            placeholder={"Hi,\n\n{{description}}\n\nPlease take a few minutes to support this campaign.\n\n{{campaign_name}}\nTake action: {{take_action_url}}\n\nBill: {{bill_line}}\n\nWhat to say:\n{{script}}\n\nHelpful links:\n{{related_links}}"}
          />
          <small>{emailBody.length}/5000</small>
        </div>

        <div className="campaign-create__preview-panes campaign-create__preview-panes--single">
          <section className="campaign-create__preview-pane campaign-create__preview-pane--email">
            <div className="campaign-create__preview-email-header">
              <h3 className="campaign-create__preview-title">Email preview</h3>
              <div className="campaign-create__preview-actions">
                <button
                  type="button"
                  className="campaign-btn campaign-btn--primary campaign-create__preview-copy-btn"
                  onClick={() => handleCopyPreview(emailPreview)}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: 18, color: "inherit" }} aria-hidden="true" />
                  Copy
                </button>
                <button
                  type="button"
                  className="campaign-btn"
                  onClick={() => handleOpenEmailApp(emailPreview)}
                >
                  Open in email app
                </button>
              </div>
            </div>
            <div className="campaign-create__email-preview">
              <p className="campaign-create__email-preview-subject">
                <strong>Subject:</strong> {emailPreview.subject || "(add a subject)"}
              </p>
              <pre className="campaign-create__email-preview-body">
                {emailPreview.body
                  ? emailPreview.body.split("\n").map((line, i) => {
                      const parsed = parseEmailPreviewLine(line, name);
                      return (
                        <React.Fragment key={i}>
                      {parsed ? (
                        <>
                          <strong className={parsed.isName ? "campaign-create__email-preview-name" : undefined}>
                            {parsed.label}
                          </strong>
                          {parsed.isPhone ? (
                            <strong className="campaign-create__email-preview-phone">{parsed.rest}</strong>
                          ) : (
                            parsed.rest
                          )}
                        </>
                      ) : (
                        line
                      )}
                          {"\n"}
                        </React.Fragment>
                      );
                    })
                  : "(add message body)"}
              </pre>
            </div>
          </section>

          <section className="campaign-create__preview-pane campaign-create__preview-pane--link">
            <h3 className="campaign-create__preview-title">Take-action page</h3>
            <p className="campaign-create__preview-link-note">
              Opens a new tab showing the supporter take-action page with your current campaign
              details, bill, script, and call targets.
            </p>
            {!isProfilePublic ? (
              <p className="campaign-detail__warning" style={{ margin: "4px 0 0" }}>
                Your profile is currently private. Turn on <strong>Make my profile public</strong> in
                Account Details so supporters can open your organizer profile from this page.
              </p>
            ) : null}
            <button
              type="button"
              className="campaign-btn campaign-btn--primary campaign-create__preview-view-btn"
              onClick={handleViewTakeActionPreview}
            >
              View
            </button>
          </section>
        </div>
      </>
    );
  };

  const renderSchedule = () => {
    const minStartValue = dayjs().add(1, 'minute');
    const minEndValue = dayjs(minEndTime);

    return (
    <>
      <h2 className="campaign-create__step-title">Schedule</h2>
      <p className="campaign-create__step-subtitle">
        Set when this campaign&apos;s take-action page (and verified calls, if enabled) should run.
      </p>
      <p className="campaign-create__schedule-note">
        {scheduleLocked
          ? "This campaign has already started, so its start time and time zone are locked. You can extend the end date to keep it running longer."
          : SCHEDULE_LOCK_NOTICE}
      </p>
      <div className="campaign-field">
        <label htmlFor="campaign-timezone">Timezone</label>
        <select
          id="campaign-timezone"
          value={selectedTimezone}
          disabled={scheduleLocked}
          onChange={(e) => {
            const nextTimezone = e.target.value;
            const startWall = toTimezoneDateTimeValue(startDate, selectedTimezone);
            const endWall = toTimezoneDateTimeValue(endDate, selectedTimezone);
            setSelectedTimezone(nextTimezone);
            const nextStart = parseTimezoneDateTimeValue(startWall, nextTimezone);
            const nextEnd = parseTimezoneDateTimeValue(endWall, nextTimezone);
            if (nextStart) setStartDate(nextStart);
            if (nextEnd) setEndDate(nextEnd);
          }}
        >
          {TIMEZONE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <small>
          {scheduleLocked
            ? "The time zone is locked while this campaign is running."
            : "Start and end times below are in this timezone."}
        </small>
      </div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="campaign-create__schedule-fields" style={{ gap: '24px' }}>
          <div className="campaign-field campaign-field--datetime">
            <label id="campaign-starts-label">Starts</label>
            {scheduleLocked ? (
              <p className="campaign-create__locked-value" aria-labelledby="campaign-starts-label">
                {formatDateWithTimezone(startDate, selectedTimezone)}
                <span className="campaign-create__locked-tag">Locked</span>
              </p>
            ) : (
              <DateTimePicker
                timezone={selectedTimezone}
                value={dayjs(startDate)}
                minDateTime={minStartValue}
                onChange={(newValue) => {
                  if (newValue && newValue.isValid()) {
                    setStartDate(newValue.toDate());
                  }
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: { bgcolor: "#fff", borderRadius: 1 }
                  }
                }}
              />
            )}
          </div>
          <div className="campaign-field campaign-field--datetime">
            <label>Ends</label>
            <DateTimePicker
              timezone={selectedTimezone}
              value={dayjs(endDate)}
              minDateTime={minEndValue}
              onChange={(newValue) => {
                if (newValue && newValue.isValid()) {
                  setEndDate(newValue.toDate());
                }
              }}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  sx: { bgcolor: "#fff", borderRadius: 1 }
                }
              }}
            />
          </div>
        </div>
      </LocalizationProvider>
      {scheduleLocked ? (
        <p className="campaign-field__hint">You can only move the end date later, never earlier.</p>
      ) : null}
      {lockedEndError ? <p className="campaign-detail__warning">{lockedEndError}</p> : null}
      {!scheduleLocked && startInPast ? (
        <p className="campaign-detail__warning">Start time must be in the future.</p>
      ) : null}
      <div className="campaign-create__review-row campaign-create__schedule-duration">
        <span>Duration</span>
        <span>{getDurationLabel(startDate, endDate)}</span>
      </div>
    </>
    );
  };

  const renderBudget = () => (
    <>
      <h2 className="campaign-create__step-title">Budget</h2>
      <div className="campaign-field">
        <label htmlFor="campaign-max-calls">Maximum Calls</label>
        <input
          id="campaign-max-calls"
          value={maxCallsText}
          onChange={(e) => setMaxCallsText(e.target.value.replace(/[^0-9]/g, ""))}
          onBlur={() => {
            const value = parseInt(maxCallsText, 10) || MIN_CALLS;
            if (value < MIN_CALLS) setMaxCallsText(String(MIN_CALLS));
          }}
          placeholder="100"
        />
        <small>Minimum: {MIN_CALLS} calls</small>
      </div>
      <div className="campaign-create__review-row">
        <span>Cost per call</span>
        <span>${COST_PER_CALL.toFixed(2)}</span>
      </div>
      <div className="campaign-create__review-row">
        <span>Maximum calls</span>
        <span>{maxCalls.toLocaleString()}</span>
      </div>
      <div className="campaign-create__review-row">
        <strong>Total Budget</strong>
        <strong>${totalCost.toFixed(2)}</strong>
      </div>
      <p className="campaign-filter-note" style={{ marginTop: 10 }}>
        You&apos;ll only be charged for verified calls actually made. Unused budget can be refunded or
        applied to future campaigns.
      </p>
    </>
  );

  const renderReview = () => (
    <>
      <h2 className="campaign-create__step-title">Review</h2>
      <div className="campaign-create__review-row">
        <span>Campaign Name</span>
        <span>{name}</span>
      </div>
      {description ? (
        <div className="campaign-create__review-row">
          <span>Description</span>
          <span>{description}</span>
        </div>
      ) : null}
      {actionScript ? (
        <div className="campaign-create__review-row">
          <span>Script</span>
          <span className="campaign-create__review-script">{actionScript}</span>
        </div>
      ) : null}
      {billMode === "in_app" && selectedBill ? (
        <div className="campaign-create__review-row">
          <span>Target Bill</span>
          <span>{getCampaignBillTypeLabel(selectedBill.type, selectedBill.number, selectedBill.congress)}</span>
        </div>
      ) : null}
      {billMode === "external" ? (
        <div className="campaign-create__review-row">
          <span>External Bill Link</span>
          <span>{externalBill.link_name || "—"}</span>
        </div>
      ) : null}
      <div className="campaign-create__review-row">
        <span>Related links</span>
        <span>{validLinks.length}</span>
      </div>
      {validLinks.length > 0 ? (
        <div className="campaign-create__review-targets">
          {validLinks.map((link, i) => (
            <div key={`${link.name}-${i}`} className="campaign-create__review-target">
              <strong>{link.name}</strong>
              <div>{link.url}</div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="campaign-create__review-row">
        <span>Email subject</span>
        <span>{emailSubject || "—"}</span>
      </div>
      <div className="campaign-create__review-row">
        <span>Call targets</span>
        <span>
          {repSource === "custom_list"
            ? `${validTargets.length} custom ${validTargets.length === 1 ? "number" : "numbers"}`
            : "Viewer's own reps"}
        </span>
      </div>
      {repSource === "custom_list" && validTargets.length > 0 ? (
        <div className="campaign-create__review-targets">
          {validTargets.map((t, i) => (
            <div key={`${t.name}-${t.phone}-${i}`} className="campaign-create__review-target">
              <strong>{t.name}</strong>
              {t.title ? ` · ${t.title}` : ""}
              <div>{t.phone}</div>
              {t.url ? <div>{t.url}</div> : null}
            </div>
          ))}
        </div>
      ) : null}
      <div className="campaign-create__review-row">
        <span>Verified calling</span>
        <span>{verifiedCalling ? "On (paid)" : "Off (free)"}</span>
      </div>
      <div className="campaign-create__review-row">
        <span>Start Date</span>
        <span>{formatDateWithTimezone(startDate, selectedTimezone)}</span>
      </div>
      <div className="campaign-create__review-row">
        <span>End Date</span>
        <span>{formatDateWithTimezone(endDate, selectedTimezone)}</span>
      </div>
      {scheduleLocked ? null : (
        <p className="campaign-create__schedule-note">{SCHEDULE_LOCK_NOTICE}</p>
      )}
      {verifiedCalling ? (
        <>
          <div className="campaign-create__review-row">
            <span>Max Calls</span>
            <span>{maxCalls.toLocaleString()}</span>
          </div>
          <div className="campaign-create__review-row">
            <strong>Total Budget</strong>
            <strong>${totalCost.toFixed(2)}</strong>
          </div>
          <p className="campaign-filter-note" style={{ marginTop: 10 }}>
            {isEditing
              ? "Save your changes, then complete payment to activate verified calling."
              : "After creating, you'll complete payment to activate verified calling."}
          </p>
        </>
      ) : (
        <p className="campaign-filter-note" style={{ marginTop: 10 }}>
          This campaign is free. Publish when you are ready, then share the take-action link from the campaign page.
        </p>
      )}
    </>
  );

  if (loadingCampaign) {
    return (
      <div className="campaign-create">
        <div className="campaign-loading">
          <CircularProgress size={24} />
          <p style={{ marginTop: 10 }}>Loading campaign...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-create">
      <div className="campaign-create__header">
        <h1 className="campaign-create__title">{isEditing ? "Edit Campaign" : "Create Campaign"}</h1>
      </div>

      {!verifiedUser ? (
        <div className="campaign-create__verify-hint" role="status">
          <button
            type="button"
            className="campaign-create__verify-hint-toggle"
            onClick={() => setVerifyHintExpanded((open) => !open)}
            aria-expanded={verifyHintExpanded}
          >
            <VerifiedUserOutlinedIcon className="campaign-create__verify-hint-icon" aria-hidden />
            <span className="campaign-create__verify-hint-title">Verified badge for supporters</span>
            {verifyHintExpanded ? (
              <ExpandLessIcon className="campaign-create__verify-hint-chevron" aria-hidden />
            ) : (
              <ExpandMoreIcon className="campaign-create__verify-hint-chevron" aria-hidden />
            )}
          </button>
          {verifyHintExpanded ? (
            <div className="campaign-create__verify-hint-body">
              <p className="campaign-create__verify-hint-text">
                Verify your identity so supporters see a verified badge next to your name on your take-action page.
              </p>
              <button
                type="button"
                className="campaign-create__verify-hint-link"
                onClick={() => setShowVerifyModal(true)}
              >
                Verify identity
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <nav className="campaign-create__steps" aria-label="Campaign setup steps">
        {steps.map((s, idx) => {
          const isCurrent = stepKey === s.key;
          const isPast = idx < stepIndex;
          return (
            <React.Fragment key={s.key}>
              <button
                type="button"
                className={[
                  "campaign-create__step",
                  isCurrent ? "campaign-create__step--current" : "",
                  isPast ? "campaign-create__step--past" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => {
                  if (!submitting) goToStep(s.key);
                }}
                disabled={submitting}
                aria-current={isCurrent ? "step" : undefined}
                aria-label={`Go to ${s.title} step`}
              >
                <span className="campaign-create__step-circle">
                  {isPast ? (
                    <span className="campaign-create__step-check" aria-hidden="true">✓</span>
                  ) : (
                    <span className="campaign-create__step-number">{idx + 1}</span>
                  )}
                </span>
                <span className="campaign-create__step-label">{s.title}</span>
              </button>
              {idx < steps.length - 1 ? (
                <span
                  className={`campaign-create__step-line ${isPast ? "campaign-create__step-line--active" : ""}`}
                  aria-hidden="true"
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="campaign-create__panel">
        {stepKey === "details" && renderDetails()}
        {stepKey === "bill" && renderBill()}
        {stepKey === "preview" && renderPreview()}
        {stepKey === "schedule" && renderSchedule()}
        {stepKey === "budget" && renderBudget()}
        {stepKey === "review" && renderReview()}
      </div>

      <div className="campaign-create__footer">
        <button
          type="button"
          className="campaign-btn"
          onClick={onBack}
          disabled={submitting || exitSaving}
        >
          {stepIndex === 0 ? "Cancel" : "Back"}
        </button>
        <button
          type="button"
          className="campaign-btn campaign-btn--primary"
          onClick={onNext}
          disabled={!canProceed() || submitting}
          aria-disabled={!canProceed() || submitting}
        >
          {submitting
            ? stepKey === "review"
              ? "Saving..."
              : "Continue"
            : stepKey === "review"
              ? isEditing
                ? verifiedCalling
                  ? "Save Changes"
                  : loadedCampaignStatus === "draft"
                    ? "Publish Campaign"
                    : "Save Changes"
                : verifiedCalling
                  ? "Create Campaign"
                  : "Publish Campaign"
              : "Continue"}
        </button>
      </div>

      <CampaignPublishBlockersDialog
        open={publishBlockersOpen}
        onClose={() => setPublishBlockersOpen(false)}
        issues={publishIssues}
        onGoToStep={(step) => goToStep(step)}
      />

      <Dialog
        open={exitDialogOpen}
        onClose={() => !exitSaving && setExitDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            pr: 6,
            pt: 2,
            pb: 0.5,
            fontWeight: 800,
            fontSize: "1.125rem",
            color: "#0f172a",
          }}
        >
          Leave campaign setup?
          <IconButton
            aria-label="Close"
            onClick={() => setExitDialogOpen(false)}
            disabled={exitSaving}
            size="small"
            sx={{
              position: "absolute",
              right: 10,
              top: 10,
              color: "#0f172a",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 1.5, px: 3 }}>
          {canSaveDraftOnCancel ? (
            <Typography sx={{ fontSize: "15px", lineHeight: 1.45, color: "#334155" }}>
              You can save your progress as a draft before you leave.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "15px", lineHeight: 1.45, color: "#334155" }}>
              You haven&apos;t entered enough required details yet to save a draft. Leaving now will
              discard this unsaved campaign.
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            pt: 0.5,
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Button
            onClick={handleDiscardExit}
            disabled={exitSaving}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#fff",
              borderRadius: "10px",
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              "&.Mui-disabled": { bgcolor: "#fca5a5", color: "#fff" },
            }}
          >
            Exit without saving
          </Button>
          <Button
            onClick={handleSaveDraftExit}
            disabled={!canSaveDraftOnCancel || exitSaving}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              bgcolor: "#047857",
              "&:hover": { bgcolor: "#065f46" },
            }}
          >
            {exitSaving ? "Saving..." : "Save draft & exit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={prefillModalOpen}
        onClose={() => !prefillLoading && setPrefillModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Prefill from Congress</DialogTitle>
        <DialogContent>
          <div className="campaign-field" style={{ marginTop: 8 }}>
            <label htmlFor="prefill-state">State (2-letter code) *</label>
            <input
              id="prefill-state"
              value={prefillState}
              onChange={(e) => setPrefillState(e.target.value.toUpperCase())}
              placeholder="e.g., CA"
              maxLength={2}
            />
          </div>
          <div className="campaign-field">
            <label htmlFor="prefill-district">District Number</label>
            <input
              id="prefill-district"
              value={prefillDistrict}
              onChange={(e) => setPrefillDistrict(e.target.value)}
              placeholder="e.g., 12"
              type="number"
            />
            <p style={{ color: "#000", fontSize: "14px", marginTop: "8px", fontWeight: 500, lineHeight: 1.4 }}>
              Leave blank to fetch only Senators. Add a district to also fetch the Representative.
            </p>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPrefillModalOpen(false)}
            disabled={prefillLoading}
            sx={{ textTransform: "none", color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            onClick={executePrefillTargets}
            disabled={prefillLoading || prefillState.trim().length !== 2}
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#0f172a", "&:hover": { bgcolor: "#334155" } }}
          >
            {prefillLoading ? "Fetching..." : "Prefill"}
          </Button>
        </DialogActions>
      </Dialog>

      <UpgradePrompt
        open={upgradePrompt.open}
        onClose={() => setUpgradePrompt((prev) => ({ ...prev, open: false }))}
        upgradeUrl={upgradePrompt.upgradeUrl}
      />

      <VerifyIdentityModal
        isOpen={showVerifyModal}
        onClose={() => {
          setShowVerifyModal(false);
          loadUserContext?.();
        }}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          icon={false}
          sx={{
            width: "100%",
            borderRadius: 2,
            fontWeight: 600,
            bgcolor: "#0f172a",
            color: "#fff",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
            "& .MuiIconButton-root": { color: "#fff" },
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
